/**
 * SPA mount: fail-closed config → repository → providers → auth → router.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { isDatasetNotFoundError, isOhlcUnavailableError } from '@meridian/core';

import { AuthProvider } from '@/auth/auth-context';
import { ErrorBoundary } from '@/components/primitives/error-boundary';
import { loadConfig } from '@/config';
import { createRepository } from '@/data/create-repository';
import { isDataError, type DataErrorCode } from '@/data/errors';
import { RepositoryProvider } from '@/data/repository-context';
import { ThemeProvider } from '@/state/theme-context';

import { router } from './router';

import './styles/tokens.dark.css';
import './styles/tokens.light.css';
import './styles/base.css';
import './styles/typography.css';
import './styles/layout.css';
import './styles/a11y.css';

/**
 * Outcomes an identical second GET cannot change. A deep link to an unknown or
 * delisted ticker is a supported path and fires four queries at once; retrying
 * these only doubles the request count on the paths that already failed.
 */
const NON_RETRYABLE_CODES: readonly DataErrorCode[] = [
  'NOT_FOUND',
  'DELISTED',
  'VALIDATION',
  'UNAUTHORIZED',
] as const;

/** Attempts after the first; transport faults (NETWORK, INTERNAL) get one more. */
const QUERY_RETRY_LIMIT = 1;

function isRetryable(error: unknown): boolean {
  if (isDatasetNotFoundError(error)) return false;
  if (isOhlcUnavailableError(error)) return false;
  if (isDataError(error)) return !NON_RETRYABLE_CODES.includes(error.code);
  return true;
}

const config = loadConfig();
const repository = createRepository(config);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => failureCount < QUERY_RETRY_LIMIT && isRetryable(error),
      refetchOnWindowFocus: false,
      /* Market facts refresh on the nightly boundary; align with Cache-Control max-age=300. */
      staleTime: 300_000,
    },
  },
});

const el = document.getElementById('root');
if (el === null) {
  throw new Error('Missing #root mount node');
}

createRoot(el).render(
  <StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider config={config}>
          <RepositoryProvider repository={repository}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </RepositoryProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>,
);
